/**
 * UserSection component
 *
 * Bottom of sidebar showing logged in user info or login button.
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { User } from '../../types';

interface UserSectionProps {
  user: User | null;
  isAuthenticated: boolean;
  onLogin: () => void;
  onLogout: () => void;
}

export function UserSection({
  user,
  isAuthenticated,
  onLogin,
  onLogout,
}: UserSectionProps) {
  const { t } = useTranslation();

  if (!isAuthenticated || !user) {
    return (
      <div className="user-section" data-testid="user-section">
        <button className="login-btn" onClick={onLogin} data-testid="login-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
          </svg>
          {t('auth.login')}
        </button>
      </div>
    );
  }

  return (
    <div className="user-section" data-testid="user-section">
      <Link to="/profile" className="user-profile-link" title={t('profile.title')}>
        {user.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            className="user-avatar"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="user-avatar" />
        )}
        <div className="user-info">
          <div className="user-name">{user.name}</div>
          <div className="user-email">{user.email}</div>
        </div>
      </Link>
      <button
        className="logout-btn"
        onClick={onLogout}
        data-testid="logout-btn"
        title={t('auth.logout')}
      >
        ↪
      </button>
    </div>
  );
}

export default UserSection;
